import { create } from 'zustand'

interface User {
  id: string
  name: string
  email: string
  avatar?: string
  plan: 'free' | 'pro' | 'enterprise'
}

interface Notification {
  id: string
  type: 'info' | 'success' | 'warning' | 'error'
  title: string
  message: string
  timestamp: Date
  read: boolean
}

interface AppState {
  user: User | null
  isAuthenticated: boolean
  isCopilotOpen: boolean
  notifications: Notification[]
  theme: 'dark' | 'light'
  
  setUser: (user: User | null) => void
  toggleCopilot: () => void
  setCopilotOpen: (open: boolean) => void
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void
  markNotificationRead: (id: string) => void
  clearNotifications: () => void
  setTheme: (theme: 'dark' | 'light') => void
}

export const useAppStore = create<AppState>((set) => ({
  // User starts as null - authentication required for full functionality
  // Demo mode available at /demo with Paul as the demo user
  user: null,
  isAuthenticated: false,
  isCopilotOpen: false,
  notifications: [
    {
      id: '1',
      type: 'success',
      title: 'Print Complete',
      message: 'Your "Phone Stand v2" has finished printing successfully.',
      timestamp: new Date(Date.now() - 3600000),
      read: false,
    },
    {
      id: '2',
      type: 'info',
      title: 'New AI Features',
      message: 'Text-to-3D generation is now available in beta.',
      timestamp: new Date(Date.now() - 86400000),
      read: true,
    },
  ],
  theme: 'dark',

  setUser: (user) => set({ user, isAuthenticated: !!user }),
  
  toggleCopilot: () => set((state) => ({ isCopilotOpen: !state.isCopilotOpen })),
  
  setCopilotOpen: (open) => set({ isCopilotOpen: open }),
  
  addNotification: (notification) =>
    set((state) => ({
      notifications: [
        {
          ...notification,
          id: Math.random().toString(36).substring(7),
          timestamp: new Date(),
          read: false,
        },
        ...state.notifications,
      ],
    })),
  
  markNotificationRead: (id) =>
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n
      ),
    })),
  
  clearNotifications: () => set({ notifications: [] }),
  
  setTheme: (theme) => set({ theme }),
}))
